import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { breakpoint, calcRem } from '../../../../__styling'
import type { PackageThemeProps } from '../BreachWallContent/BreachWallContent'

const SWG_BUTTON_IMAGE = require('../SubscriptionPackage/SubscribeWithGoogle.svg')
const WEST_FOOTY_RED = '#9E1D22'
const WEST_FOOTY_YELLOW = '#FFC70B'

export const StyledSubscriptionPackageContainer = styled(
    'div',
)<PackageThemeProps>(({ packageOptions }) => [
    {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        alignSelf: 'flex-end',
        height: 'auto',
        border: packageOptions.isRecommended
            ? `8px solid ${tokens.thewest.colors.brand}`
            : 'none',
        marginBottom: packageOptions.isRecommended ? 0 : calcRem(8),
        padding: packageOptions.isRecommended ? 0 : calcRem(8),
        background: packageOptions.isRecommended
            ? tokens.thewest.colors.brand
            : 'transparent',
        borderRadius: calcRem(8),
        maxWidth: '100%',

        // Tablet
        [breakpoint('sm')]: {
            maxWidth: '50%',
        },
        // Desktop
        [breakpoint('lg')]: {
            maxWidth: '100%',
            padding: 0,
        },
    },
    // If grid column position is set, use this to position packages in the parent grid
    packageOptions.gridColumnPosition && {
        // On mobile, we use a single column with multiple rows
        gridColumn: '1',
        gridRow: packageOptions.gridColumnPosition.mobile,

        // Desktop
        [breakpoint('lg')]: {
            // On desktop, we use a single row with multiple columns
            gridColumn: packageOptions.gridColumnPosition.desktop,
            gridRow: '1',
        },
    },
])
export const StyledSubscriptionPackage = styled('div')(() => [
    {
        background: tokens.thewest.colors.palette.white,
        display: 'flex',
        gap: calcRem(16),
        flex: `1`,
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: calcRem(8),
        height: '100%',
        padding: calcRem(0, 24, 16, 24),

        // Desktop breakpoint
        [breakpoint('lg')]: {
            flex: `1 0 ${calcRem(975)}`, // Minimum size ensures no layout shift when selecting a print package due to extra benefits
        },
    },
])

export const StyledImageAndHeaderContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
}))

export const StyledPackageImage = styled('img')(() => ({
    objectFit: 'contain',
    width: '100%',
    height: 'auto',
    maxHeight: calcRem(250),
}))

export const StyledSubscriptionPackageHeadingContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: calcRem(12),
    width: '100%',
    alignItems: 'center',
}))

export const StyledSubscriptionPackageHeading = styled('h4')(({ theme }) => [
    {
        fontFamily: theme.fonts.serif,
        fontSize: calcRem(28),
        lineHeight: calcRem(36),
        fontWeight: 700,
        margin: 0,
        color: tokens.thewest.colors.text.weak,

        // Tablet and above
        [breakpoint('sm')]: {
            // At tablet and above, we use a single row with multiple columns
            fontSize: calcRem(32),
            lineHeight: calcRem(40),
        },
    },
])

export const StyledSubscriptionPackageSubHeading = styled('h5')(({ theme }) => [
    {
        fontFamily: theme.fonts.sansSerif,
        fontSize: calcRem(20),
        lineHeight: calcRem(24),
        fontWeight: 600,
        margin: 0,
        color: tokens.thewest.colors.text.weak,
        paddingBottom: calcRem(4),
        textAlign: 'center',

        // Tablet and above
        [breakpoint('sm')]: {
            // At tablet and above, we use a single row with multiple columns
            fontSize: calcRem(20),
            lineHeight: calcRem(24),
        },
    },
])

export const StyledCancellationMessage = styled('p')(() => [
    {
        margin: 0,
        fontSize: calcRem(12),
        fontWeight: 500,
        lineHeight: calcRem(16),
        color: tokens.thewest.colors.text.weak,
        paddingTop: calcRem(24),
    },
])

export const StyledRecommendedBanner = styled('p')(() => ({
    textTransform: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: tokens.thewest.colors.brand,
    color: tokens.thewest.colors.palette.white,
    fontWeight: 600,
    fontSize: calcRem(24),
    lineHeight: calcRem(29),
    paddingBottom: calcRem(8),
    width: '100%',
    margin: 0,

    em: {
        fontStyle: 'normal',
        color: WEST_FOOTY_YELLOW,
    },
}))

export const StyledSubscribeButtonsContainer = styled('div')(() => [
    {
        display: 'flex',
        flexDirection: 'column',
        gap: calcRem(8),
        width: '100%',
    },
])

export const StyledSubscribeButton = styled('button')<PackageThemeProps>(
    ({ buttonStyle, breachOptions }) => [
        // -- Base Styles (Applied to all packages)
        {
            background:
                'linear-gradient(270deg, rgb(220, 1, 190) 0%, rgb(170, 1, 225) 100%)',
            fontSize: calcRem(18),
            lineHeight: calcRem(16),
            color: tokens.thewest.colors.palette.white,
            padding: calcRem(16, 0),
            margin: calcRem(8, 0),
            fontWeight: 500,
            width: '100%',
            border: 'none',
            borderRadius: calcRem(4),
            cursor: 'pointer',

            '&:hover, &:focus': {
                opacity: 0.8,
            },
        },
        // -- Button Style Variations
        buttonStyle === 'solid-blue' && {
            background: tokens.thewest.colors.brand,
        },
        buttonStyle === 'solid-red' && {
            background: WEST_FOOTY_RED,
        },
        buttonStyle === 'solid-yellow' && {
            background: WEST_FOOTY_YELLOW,
            color: tokens.thewest.colors.text.weak,
            transition: 'background 0.25s ease',

            '&:active, &:focus': {
                background: '#BF9508',
            },
            '&:hover': {
                background: '#DBB309',
            },
        },
        buttonStyle === 'transparent' && {
            background: 'transparent',
            color: tokens.thewest.colors.brand,
            border: `1px solid ${tokens.thewest.colors.brand}`,
        },
        // -- Package specific styling
    ],
)

export const StyledSwgButton = styled('button')(() => ({
    cursor: 'pointer',
    maxWidth: '100%',
    width: '100%',
    padding: calcRem(0, 12),
    borderRadius: calcRem(2),
    border: `1px solid ${tokens.thewest.colors.palette.greySeal}`,
    '&::after': {
        content: "''",
        position: 'relative',
        display: 'inline-block',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url('${SWG_BUTTON_IMAGE}')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
    },
    '&:hover': {
        backgroundColor: '#f8f8f0',
    },
}))

export const StyledBenefitListContainer = styled('ul')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: calcRem(12),
    padding: 0,
    margin: 0,
    flex: '1 0 auto',
}))

export const StyledBenefitListItemContainer = styled('li')<{ color?: string }>(
    ({ color }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: calcRem(8),
        color: color ?? tokens.thewest.colors.palette.greyElephant,
        fontSize: calcRem(16),
        fontWeight: 400,

        p: {
            margin: 0,

            strong: {
                fontWeight: 600,
            },
            em: {
                color: tokens.thewest.colors.palette.westblue,
                fontStyle: 'normal',
            },
        },
        // Override the styles in the SubscriptionPackageBenefit component
        span: {
            fontWeight: 400,
        },
        // *shakes fist* damn you flexbox
        svg: {
            flexShrink: 0,
        },
    }),
)
